import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { z } from 'zod'
import { Controller } from 'react-hook-form'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { StatusDot } from '@/components/ui/StatusDot'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { NumberInput } from '@/components/ui/NumberInput'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCrudList } from '@/hooks/useCrudList'
import { useAuthority } from '@/hooks/useAuthority'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useZodForm } from '@/hooks/useZodForm'
import { applyApiErrorsToForm } from '@/lib/api-errors'
import { onValidationFail } from '@/lib/form-helpers'
import { CredentialingStepsService, type CredentialingStep } from '@/services/modules/CredentialingService'
import { toast } from '@/components/ui/Toast'

export function CredentialingStepsPage() {
  const [reload, setReload] = useState(0)
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar etapa' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(CredentialingStepsService, { size: 15, reloadKey: reload , filters: sf.filters})

  const exp = useDataTableExport({
    filename: 'steps',
    service: CredentialingStepsService as never,
    filters: sf.filters,
    columns: [], // TODO: configurar columns para export
  })
  const [openForm, setOpenForm] = useState<{ open: boolean; item?: CredentialingStep }>({ open: false })
  const [openDelete, setOpenDelete] = useState<CredentialingStep | null>(null)
  const { can } = useAuthority()
  const canCreate = can('credentialing.steps.create')
  const canEdit = can('credentialing.steps.edit')
  const canDelete = can('credentialing.steps.delete')

  async function save(payload: Record<string, unknown>) {
    if (openForm.item) { await CredentialingStepsService.update(openForm.item.id, payload); toast.success({ title: 'Atualizada', description: 'Etapa atualizada com sucesso.' }) }
    else { await CredentialingStepsService.create(payload); toast.success({ title: 'Criada', description: 'Nova etapa criada com sucesso.' }) }
    setOpenForm({ open: false }); setReload(v => v + 1)
  }
  async function del() {
    if (!openDelete) return
    try { await CredentialingStepsService.destroy(openDelete.id); toast.success('Etapa removida.'); setOpenDelete(null); setReload(v => v + 1) }
    catch { toast.error('Erro.') }
  }

  const columns: DataTableColumn<CredentialingStep>[] = [
    { key: 'order', header: 'Ordem', render: s => <span>{s.order}</span>, className: 'w-20' },
    { key: 'step',  header: 'Etapa', render: s => <span className="font-medium text-gray-800">{s.step}</span>, sortable: true },
    { key: 'status', header: 'Status', align: 'center', className: 'w-24',
      render: s => (
        <StatusDot value={s.status} />
      ),
    },
  ]

  return (
    <>
      <PageHeader title="Etapas do kanban" subtitle="Ordem do fluxo de credenciamento"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Credenciamento' }, { label: 'Etapas' }]}
        actions={canCreate ? <Button onClick={() => setOpenForm({ open: true })} iconLeft={<Plus size={14} />}>Nova etapa</Button> : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="credentialing.steps"
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        rowActions={(s) => [
          ...(canEdit ? [{ key: 'edit',   label: 'Editar',  icon: <Pencil size={14} />,  onClick: () => setOpenForm({ open: true, item: s }) }] : []),
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />,  danger: true, divider: canEdit, onClick: () => setOpenDelete(s) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <StepForm open={openForm.open} item={openForm.item} onClose={() => setOpenForm({ open: false })} onSave={save} />
      <ConfirmDialog open={!!openDelete} onClose={() => setOpenDelete(null)} onConfirm={del}
        title="Remover etapa?" description={`Tem certeza que deseja remover "${openDelete?.step}"?`} confirmLabel="Remover" variant="danger" />  
    {sf.drawer}
    </>
  )
}

const stepSchema = z.object({
  step:   z.string().trim().min(1, 'Informe o nome da etapa.').max(100, 'O nome não pode passar de 100 caracteres.'),
  order:  z.number({ message: 'Informe a ordem da etapa.' }).int('A ordem deve ser um número inteiro.').min(1, 'A ordem deve ser pelo menos 1.'),
  status: z.enum(['ATIVO', 'INATIVO']),
})
type StepFormValues = z.infer<typeof stepSchema>

function StepForm({ open, item, onClose, onSave }: { open: boolean; item?: CredentialingStep; onClose: () => void; onSave: (payload: Record<string, unknown>) => Promise<void> }) {
  const form = useZodForm(stepSchema, {
    // Novo cadastro: ordem começa vazia (null) — o usuário precisa preencher.
    defaultValues: { step: '', order: null as unknown as number, status: 'ATIVO' },
  })

  useEffect(() => {
    if (!open) return
    form.reset(item
      ? { step: item.step, order: item.order, status: item.status }
      : { step: '', order: null as unknown as number, status: 'ATIVO' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item])

  async function submit(values: StepFormValues) {
    try {
      await onSave({ step: values.step, order: values.order, status: values.status })
    } catch (e) {
      applyApiErrorsToForm(e, form.setError)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={item ? 'Editar etapa' : 'Nova etapa'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={form.handleSubmit(submit, onValidationFail)} loading={form.formState.isSubmitting}>Salvar</Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Nome da etapa"
          required
          {...form.register('step')}
          placeholder="Prospecção"
          error={form.formState.errors.step?.message}
        />
        <Controller
          name="order"
          control={form.control}
          render={({ field, fieldState }) => (
            <NumberInput
              label="Ordem"
              required
              value={field.value ?? null}
              onChange={field.onChange}
              min={1}
              placeholder="Ex.: 1"
              hint="Define a sequência no kanban. Não pode repetir entre etapas ativas."
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="status"
          control={form.control}
          render={({ field }) => (
            <StatusToggle value={field.value} onChange={field.onChange} />
          )}
        />
      </div>
    </Modal>
  )
}
